import {assert, assertAndContinue} from 'Asserts';
import {ProcessExecutionType, ProcessClientOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {assertNever} from 'util/Types';

import {UpdateTaskParams} from '../utils/updateTaskDetailsTask';
import {getAssayRunTaskDetailsSummaryRows} from './assays/getAssayRunTaskDetailsSections';
import {getAssayStepTaskDetailsSummaryRows} from './assays/getAssayStepTaskDetailsSections';
import {getAssayUnitOperationTaskDetailsSummaryRows} from './assays/getAssayUnitOperationTaskDetailsSections';
import {getProceduresTaskMetadataRows} from './procedures/getProceduresDetailsSections';
import {
  getProcessTaskDetailsMetadataRows,
  getProcessTaskDetailsSummaryRows,
} from './processes/getProcessTaskDetailsSections';
import {ProcessTaskDetailsRowContent, ProcessTaskGql} from './ProcessTaskDetailsPageConstants';
import {getRecipeRunTaskDetailsSummaryRows} from './recipes/getRecipeRunTaskDetailsSections';
import {getRecipeStepTaskDetailsSummaryRows} from './recipes/getRecipeStepTaskDetailsSections';
import {getRecipeUnitOperationTaskDetailsSummaryRows} from './recipes/getRecipeUnitOperationTaskDetailsSections';

export function getMetadataRows(processTask: ProcessTaskGql): Array<ProcessTaskDetailsRowContent> {
  switch (processTask.schema.executionType) {
    case ProcessExecutionType.DIRECT:
    case ProcessExecutionType.ENTRY:
    case ProcessExecutionType.FLOWCHART:
      return getProcessTaskDetailsMetadataRows(processTask);
    case ProcessExecutionType.PROCEDURE:
    case ProcessExecutionType.PROCEDURE_METHOD:
    case ProcessExecutionType.PROCEDURE_STEP:
      return getProceduresTaskMetadataRows(processTask);
    default:
      assertAndContinue(
        false,
        `No metadata rows defined for task of type ${assertNever(processTask.schema.executionType)}`
      );
      return [];
  }
}

export function getSummaryRows(
  processTask: ProcessTaskGql,
  onUpdateTask: (updateParams: UpdateTaskParams) => Promise<void>
): Array<ProcessTaskDetailsRowContent> {
  const isRecipesClientOrigin = (): boolean => {
    const isRecipes = processTask.schema.clientOrigin === ProcessClientOrigin.RECIPES;
    if (!isRecipes) {
      assert(
        processTask.schema.clientOrigin === ProcessClientOrigin.ASSAYS,
        `Unknown client origin ${processTask.schema.clientOrigin}`
      );
    }
    return isRecipes;
  };

  switch (processTask.schema.executionType) {
    case ProcessExecutionType.DIRECT:
    case ProcessExecutionType.ENTRY:
    case ProcessExecutionType.FLOWCHART:
      return getProcessTaskDetailsSummaryRows(processTask, onUpdateTask);
    case ProcessExecutionType.PROCEDURE:
      return isRecipesClientOrigin()
        ? getRecipeRunTaskDetailsSummaryRows(processTask, onUpdateTask)
        : getAssayRunTaskDetailsSummaryRows(processTask, onUpdateTask);
    case ProcessExecutionType.PROCEDURE_METHOD:
      return isRecipesClientOrigin()
        ? getRecipeUnitOperationTaskDetailsSummaryRows(processTask, onUpdateTask)
        : getAssayUnitOperationTaskDetailsSummaryRows(processTask, onUpdateTask);
    case ProcessExecutionType.PROCEDURE_STEP:
      return isRecipesClientOrigin()
        ? getRecipeStepTaskDetailsSummaryRows(processTask, onUpdateTask)
        : getAssayStepTaskDetailsSummaryRows(processTask, onUpdateTask);
    default:
      assertAndContinue(
        false,
        `No summary rows defined for task of type ${assertNever(processTask.schema.executionType)}`
      );
      return [];
  }
}
